import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";
import { X, PaperPlaneTilt, CalendarBlank, Image as ImageIcon, Spinner } from "@phosphor-icons/react";

const PLATFORMS = [
  { id: "facebook", label: "Facebook", color: "#1877F2", Icon: FaFacebook, tid: TID.composerPlatformFb },
  { id: "instagram", label: "Instagram", color: "#E4405F", Icon: FaInstagram, tid: TID.composerPlatformIg },
  { id: "linkedin", label: "LinkedIn", color: "#0A66C2", Icon: FaLinkedin, tid: TID.composerPlatformLi },
];

const roundToNext15 = () => {
  const d = new Date(Date.now() + 15 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function NewPostModal({ open, onClose, onCreated, accounts }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [selected, setSelected] = useState([]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(roundToNext15());
  const [campaign, setCampaign] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setUrl(""); setCaption(""); setSelected([]);
      setScheduleOn(false); setScheduledAt(roundToNext15());
      setCampaign(""); setPreview(null); setPreviewLoading(false);
    }
  }, [open]);

  // Debounced OG preview fetch when URL changes
  useEffect(() => {
    if (!open) return;
    if (!/^https?:\/\/.+/.test(url)) { setPreview(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.get("/preview", { params: { url } });
        setPreview(data);
      } catch {
        setPreview(null);
      } finally { setPreviewLoading(false); }
    }, 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [url, open]);

  if (!open) return null;

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const connected = new Set((accounts || []).filter((a) => a.connected).map((a) => a.platform));

  const submit = async (immediate) => {
    if (!url.trim()) { toast.error("URL is required"); return; }
    if (selected.length === 0) { toast.error("Select at least one platform"); return; }
    setSubmitting(true);
    try {
      const payload = {
        url: url.trim(),
        caption: caption.trim(),
        platforms: selected,
        scheduled_at: !immediate && scheduleOn ? new Date(scheduledAt).toISOString() : null,
        campaign: campaign.trim() || null,
        source: "web",
      };
      await api.post("/posts", payload);
      toast.success(immediate ? "Posting now…" : "Scheduled ✨");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create post");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#18181B] border border-[#3F3F46] p-6" style={{ borderRadius: 4 }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline">new post</div>
            <div className="font-display text-2xl mt-1">Compose your drop</div>
          </div>
          <button data-testid={TID.composerClose} onClick={onClose} className="p-1.5 text-[#71717A] hover:text-white hover:bg-[#27272A] transition-colors duration-150" style={{ borderRadius: 2 }}>
            <X size={16} weight="bold" />
          </button>
        </div>

        <label className="overline block mb-1">URL</label>
        <input data-testid={TID.composerUrl} type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="input mb-3" />

        {(previewLoading || preview) && (
          <div
            data-testid="composer-preview"
            className="border border-[#27272A] mb-4 overflow-hidden"
            style={{ borderRadius: 2 }}
          >
            {previewLoading ? (
              <div className="p-3 flex items-center gap-2 text-xs text-[#71717A]">
                <Spinner size={14} className="animate-spin" /> fetching preview…
              </div>
            ) : preview?.error ? (
              <div className="p-3 text-xs text-[#fca5a5] flex items-center gap-2">
                <ImageIcon size={12} /> couldn&apos;t load preview: {preview.error}
              </div>
            ) : (
              <div className="flex gap-3">
                {preview.image && (
                  <img
                    src={preview.image}
                    alt=""
                    className="w-24 h-24 object-cover flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <div className="flex-1 min-w-0 p-3">
                  <div className="text-[10px] font-mono text-[#71717A] uppercase tracking-widest truncate">
                    {preview.site_name || preview.domain || ""}
                  </div>
                  <div className="text-sm font-medium truncate text-white mt-0.5">
                    {preview.title || url}
                  </div>
                  {preview.description && (
                    <div className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">
                      {preview.description}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <label className="overline block mb-1">Caption</label>
        <textarea data-testid={TID.composerCaption} value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} placeholder="Say something worth clicking…" className="input mb-4" />

        <label className="overline block mb-2">Platforms</label>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {PLATFORMS.map(({ id, label, color, Icon, tid }) => {
            const on = selected.includes(id);
            const disabled = !connected.has(id);
            return (
              <button
                key={id}
                data-testid={tid}
                type="button"
                onClick={() => !disabled && toggle(id)}
                className="flex items-center justify-center gap-2 py-2.5 text-sm transition-colors duration-150"
                style={{
                  border: `1px solid ${on ? color : "#27272A"}`,
                  color: on ? color : disabled ? "#3F3F46" : "#A1A1AA",
                  background: on ? `${color}18` : "transparent",
                  cursor: disabled ? "not-allowed" : "pointer",
                  borderRadius: 2,
                }}
                title={disabled ? "Connect this account first" : ""}
              >
                <Icon size={14} /> {label}
              </button>
            );
          })}
        </div>
        {selected.some((p) => !connected.has(p)) && (
          <div className="text-xs text-[#fca5a5] mb-3">Some platforms aren&apos;t connected — those will fail. Connect them in the sidebar.</div>
        )}

        <label className="flex items-center gap-2 mb-3 text-sm text-[#A1A1AA]">
          <input
            data-testid={TID.composerScheduleToggle}
            type="checkbox"
            checked={scheduleOn}
            onChange={(e) => setScheduleOn(e.target.checked)}
            className="accent-[#FF4500] w-4 h-4"
          />
          <CalendarBlank size={14} /> Schedule for later
        </label>
        {scheduleOn && (
          <input
            data-testid={TID.composerScheduleAt}
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="input mb-4"
          />
        )}

        <label className="overline block mb-1">Campaign (utm_campaign) <span className="normal-case text-[10px] tracking-normal text-[#71717A]">— optional</span></label>
        <input
          data-testid="composer-campaign"
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
          placeholder="e.g. spring_launch_2026"
          className="input mb-4"
        />

        <div className="grid grid-cols-2 gap-2 mt-6">
          <button
            data-testid={TID.composerPostNow}
            onClick={() => submit(true)}
            disabled={submitting}
            className="btn btn-primary"
          >
            <PaperPlaneTilt size={14} weight="fill" /> post now
          </button>
          <button
            data-testid={TID.composerSchedule}
            onClick={() => { if (!scheduleOn) { setScheduleOn(true); return; } submit(false); }}
            disabled={submitting}
            className="btn btn-secondary"
          >
            <CalendarBlank size={14} weight="fill" /> {scheduleOn ? "schedule" : "pick a time"}
          </button>
        </div>
      </div>
    </div>
  );
}
